import { AnyAction, ThunkAction, ThunkDispatch } from '@reduxjs/toolkit';

import { AppNotification } from '../types/appTypes';
import { RequestStatus } from './utils/requestStatus';
import { ActivityDefinition, ActivityDefinitionId } from '../types/library';
import {
  ConfigCatalogDto,
  CustomActivityDto,
  CustomActivityId,
} from '../types/customActivity';
import { ActivityStatisticsDto } from '../types/statistics';
import {
  CaseDto,
  EmrLinkDto,
  MovementResultsDto,
  TreatmentPlanDto,
  TreatmentPlanTemplateDto,
  UserDto,
  UserRole,
} from '../types/backendType';
import { TreatmentPlanId } from '../models/TreatmentPlan';
import { TreatmentPlanTemplateId } from '../models/TreatmentPlanTemplate';
import { LoadedRange } from './utils/resultsCoverage';
import { UserId } from '../models/User';

export type TreatmentPlansState = {
  treatmentPlans: Record<UserId, TreatmentPlanDto[]>;
  movementsResults: Record<UserId, MovementResultsDto[]>;
  /**
   * Which stretches of execution history have actually been loaded, per patient.
   *
   * A LIST of ranges, not a single "how far back" number, because a clinician
   * can ask for a range ending in the past — so coverage is genuinely disjoint.
   * It is also the only way another screen can tell "no results" apart from
   * "not loaded for that period", which the Plans tab's New-plan gate depends
   * on. See utils/resultsCoverage.ts.
   */
  loadedResultsRanges: Record<UserId, LoadedRange[]>;
};

export type UsersState = {
  users: Record<UserId, UserDto>;
};

export type CasesState = {
  cases: Record<UserId, CaseDto[]>;
};

export type EmrLinkState = {
  // Keyed by Percix user id. A patient can have one EmrLink per adapter
  // (v1 has only WebPT, so this is usually 0 or 1 entries).
  links: Record<UserId, EmrLinkDto[]>;
};

export type NotificationsState = {
  notifications: AppNotification[];
};

export const requestStatusNames = [
  'user',
  'fetchActivitiesResultsByUserId',
  'fetchAssignTreatmentPlans',
  'deleteTreatmentPlan',
  'deleteTreatmentPlanTemplate',
  'fetchActivityDefinitions',
  'createTreatmentPlanTemplate',
  'editTreatmentPlanTemplate',
  'fetchTreatmentPlanTemplates',
  'assignTreatmentPlan',
  'createNewUser',
  'updateUser',
  'updateTreatmentPlan',
  'saveAndAssignTreatmentPlan',
  'fetchCases',
  'createCase',
  'fetchUsers',
  'fetchCustomActivities',
  'saveCustomActivity',
  'fetchActivityStatistics',
] as const;

export type RequestStatusName = (typeof requestStatusNames)[number];

export type AppState = {
  userId?: UserId;
  role?: UserRole;
  currentPatientId: UserId | null | undefined;
  selectedCaseId: string | null;
  currentTreatmentPlanId: TreatmentPlanId | undefined;
  requestsStatus: Record<RequestStatusName, RequestStatus>;
  measurementSystem: string;
  editTemplateId?: TreatmentPlanId | undefined;
  appVersion?: string;
};

export type LibraryState = {
  treatmentPlanTemplates: Record<TreatmentPlanTemplateId, TreatmentPlanTemplateDto>;
  activityDefinitions: Record<ActivityDefinitionId, ActivityDefinition>;
};

export type CustomActivitiesState = {
  customActivities: Record<CustomActivityId, CustomActivityDto>;
  configCatalog?: ConfigCatalogDto;
};

export type StatisticsState = {
  activityStatistics?: ActivityStatisticsDto;
};

export type PatientDetailsEmr = {
  firstName?: string;
  lastName?: string;
  emrId?: string;
  dob?: string;
};
export type AvailableAction = 'autoFillTreatmentPlan' | 'autoFillResults';
export type ExtensionPage =
  | { pageName: 'noContentAvailable' }
  | { pageName: 'treatmentPlans'; emrPatientId: string }
  | { pageName: 'sessions'; emrPatientId: string };

export type ExtensionStatus =
  | { state: 'unknown' }
  | { state: 'notInstalled' }
  | {
      state: 'connected';
      extensionVersion: string;
      supportedActions: string[];
    };

export type EmrState = {
  isExtensionMode?: boolean | undefined;
  urlInActiveTab?: string | undefined;
  patientDetails?: PatientDetailsEmr;
  availableActions?: AvailableAction[];
  extensionPage: ExtensionPage;
  extensionStatus: ExtensionStatus;
};

export type RootState = {
  appState: AppState;
  usersState: UsersState;
  treatmentPlansState: TreatmentPlansState;
  notificationsState: NotificationsState;
  libraryState: LibraryState;
  customActivitiesState: CustomActivitiesState;
  statisticsState: StatisticsState;
  casesState: CasesState;
  emrState: EmrState;
  emrLinkState: EmrLinkState;
};

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  AnyAction
>;

export type Dispatch = ThunkDispatch<RootState, unknown, AnyAction>;
